// src/components/ws/WsSheets.jsx
// 出席記録（全画面）の「小テスト・成績」「打刻明細・変更履歴」タブ【2026-10-13】
//
//   書き出しのシートを、そのまま画面で見る。中身はプレビューAPI（/api/reports/.../preview）で、
//   書き出したファイルと同じデータから作っている。
//   上の付箋で、どのシートを見るかを切り替える。
import React, { useEffect, useMemo, useState } from 'react';
import { useColWidths, ColGrip } from './useColWidths';
import { FONT_STEPS, FONT_LABELS, FONT_KEY, loadMarks, loadHeading, loadFontStep, writePref } from './wsCommon';

// 横スクロールしても左に残す列と、その幅（px）
const PIN_WIDTH = { '学籍番号': 104, '氏名': 130, '名前': 130, '授業名': 160 };
const CELL_COLOR = { '出席': '#15803d', '遅刻': '#b45309', '早退': '#0e7490', '欠席': '#b91c1c', '公欠': '#6d28d9',
                     '◯': '#15803d', '△': '#b45309', '▲': '#0e7490', '×': '#b91c1c', '◎': '#6d28d9' };

export default function WsSheets({ classId, isAll = false, periodQuery = '', sheets, emptyHint, setErr }) {
  const [showing, setShowing] = useState(sheets[0].key);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fontStep, setFontS] = useState(loadFontStep);

  useEffect(() => {
    if (!sheets.some(s => s.key === showing)) setShowing(sheets[0].key);
  }, [sheets, showing]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try {
        const p = new URLSearchParams();
        p.append('limit', 'all');
        if (!isAll) p.append('format', 'matrix');
        if (showing !== 'register') p.append('sheet', showing);
        if (loadMarks() === 'symbol') p.append('marks', 'symbol');
        if (loadHeading() === 'number') p.append('heading', 'number');
        if (isAll && periodQuery) new URLSearchParams(periodQuery).forEach((v, k) => p.append(k, v));
        const url = (isAll ? '/api/reports/all/preview' : '/api/reports/class/' + classId + '/preview') + '?' + p.toString();
        const res = await fetch(url, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
        const d = await res.json();
        if (!res.ok || !d.success) throw new Error(d.message || '表の取得に失敗しました');
        if (alive) setPreview(d);
      } catch (e) {
        if (alive) { setPreview(null); setErr(e.message); }
      } finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, [classId, isAll, periodQuery, showing]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeFont = (d) => {
    const v = Math.max(0, Math.min(FONT_STEPS.length - 1, fontStep + d));
    setFontS(v); writePref(FONT_KEY, v);
  };

  // 列の幅（エクセルのように見出しの境目をドラッグ）。シートごとに覚える【2026-10-13】
  const colKeys = useMemo(() => ((preview && preview.columns) || []).map(c => String(c)), [preview]);
  const cols = useColWidths((isAll ? 'all.' : '') + 'sheet.' + showing, colKeys, { defaultWidth: k => PIN_WIDTH[k] || 90 });

  const pins = useMemo(() => {
    const map = {};
    let left = 0;
    const cols = (preview && preview.columns) || [];
    for (let i = 0; i < cols.length; i++) {
      const w = PIN_WIDTH[cols[i]];
      if (!w || left + w > 320) break;
      map[i] = { left, width: w };
      left += w;
    }
    if (Object.keys(map).length >= cols.length) return {};
    return map;
  }, [preview]);
  const pinStyle = (i) => {
    const p = pins[i];
    if (!p) return {};
    if (cols.active) {
      // 幅を変えたあとは、左に固定する位置も変えた幅で数える
      let left = 0;
      for (let j = 0; j < i; j++) left += cols.widthOf(colKeys[j]);
      return { position: 'sticky', left, zIndex: 2, boxShadow: pins[i + 1] ? undefined : '2px 0 0 #d1d5db' };
    }
    return { position: 'sticky', left: p.left, width: p.width, minWidth: p.width, maxWidth: p.width,
             overflow: 'hidden', textOverflow: 'ellipsis', zIndex: 2,
             boxShadow: pins[i + 1] ? undefined : '2px 0 0 #d1d5db' };
  };

  return (
    <div className="ws-sheets">
      <div className="ws-toolbar">
        <div className="sheet-tabs" role="tablist" style={{ border: 'none', padding: 0, background: 'transparent' }}>
          {sheets.map(t => (
            <button key={t.key} type="button" role="tab" aria-selected={showing === t.key}
                    className={'sheet-tab' + (showing === t.key ? ' active' : '')} onClick={() => setShowing(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        {cols.active && (
          <button type="button" className="ws-link-btn" onClick={cols.resetAll} title="ドラッグで変えた列の幅を、全部元に戻す">列の幅を戻す</button>
        )}
        <span className="ws-toolbar-gap" />
        <div className="ws-seg" role="group" aria-label="文字の大きさ">
          <button type="button" className="ws-seg-btn" onClick={() => changeFont(-1)} disabled={fontStep === 0} aria-label="文字を小さく">－</button>
          <span className="ws-seg-label">{FONT_LABELS[fontStep]}</span>
          <button type="button" className="ws-seg-btn" onClick={() => changeFont(1)} disabled={fontStep === FONT_STEPS.length - 1} aria-label="文字を大きく">＋</button>
        </div>
      </div>
      {(() => {
        const desc = (sheets.find(s => s.key === showing) || {}).desc;
        return desc ? <div className="ws-muted ws-sheet-desc">{desc}</div> : null;
      })()}

      <div className="ws-table-box">
        {busy && !preview ? <p className="ws-muted" style={{ padding: '1.2rem' }}>読み込み中…</p>
          : !preview ? <p className="ws-muted" style={{ padding: '1.2rem' }}>表を取得できませんでした。</p>
          : preview.rows.length === 0 ? (
            <div className="ws-muted" style={{ padding: '1.5rem', textAlign: 'center', lineHeight: 1.9 }}>
              このシートに出せるデータがありません。
              {emptyHint && <div>{emptyHint}</div>}
            </div>
          ) : (
            <table ref={cols.tableRef} className={'ws-sheet-table ' + cols.className}
                   style={{ fontSize: FONT_STEPS[fontStep] + 'rem', opacity: busy ? 0.6 : 1, ...(cols.tableStyle || {}) }}>
              {cols.colgroup}
              <thead>
                <tr>{preview.columns.map((c, i) => (
                  <th key={i} style={{ ...pinStyle(i), zIndex: pins[i] ? 4 : 3, background: '#f9fafb' }}>
                    {c}<ColGrip cols={cols} k={String(c)} label={String(c)} />
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {preview.rows.map((r, ri) => (
                  <tr key={ri}>
                    {r.map((v, ci) => {
                      const color = CELL_COLOR[v];
                      return (
                        <td key={ci} title={pins[ci] ? String(v == null ? '' : v) : undefined}
                            style={{ ...pinStyle(ci), ...(color ? { color, textAlign: 'center', fontWeight: 700 } : {}) }}>
                          {v === '' || v == null ? '-' : String(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {preview && preview.rows.length > 0 && (
        <div className="ws-muted ws-sheet-foot">
          全{preview.total}行
          {preview.legend && <span style={{ marginLeft: '0.8rem' }}>{preview.legend}</span>}
          {preview.truncated && '（多すぎるため先頭' + preview.rows.length + '行を表示。ファイルには全行入ります）'}
        </div>
      )}
    </div>
  );
}
